// SB6-ANA-FILT-05 / SB6-ANA-FILT-06 — one generic filter level rendered
// from a server `FacetAxis`. A single data-driven component: the cascade
// (options, counts, per-option `disabled`, whole-level `disabled`) is
// server-authoritative (D1), so every level (Status, Review, Component,
// Location, Severity) shares this UI. The L3 Kind level uses the bespoke
// `KindLevel` (its option buttons carry an inner advanced-filter icon),
// and the L4 Defect Type tree level was removed in 7.2.0 (D3).
//
//   ┌ L4 · COMPONENT ⓘ ──────────────┐
//   │ [RB1 (97)] [RB2 (111)] [RB3 (111)]│
//   │ ⓘ All Components  (bypass hint)   │
//   └──────────────────────────────────┘
//
// Per-option counts render in the count unit chosen in Settings.
// Component options carry a hover tooltip resolved from
// `FacetOption.meta` (full group_code name + serial). Info tooltips
// render Markdown (D11) via `MarkdownInfo`.
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import type { FacetAxis, FacetOption } from "@aeriosiq/types";
// SB6-ANA-UI-01 — the ToggleButton density + count-badge style this file
// pioneered are now the shared `compactToggleSx` / `compactCountBadgeSx`
// presets in packages/ui. Consume them; the inline copies are deleted. The
// 13-px font compactToggleSx adds is a no-op for `size="small"` toggles (MUI
// already renders them at 13px) so there is zero visual change here.
import { compactToggleSx, compactCountBadgeSx } from "@aeriosiq/ui";
import { BypassHintSlot } from "./BypassHint.js";
import { LevelHeader } from "./LevelHeader.js";
import {
  FILTER_LEVEL_BYPASS,
  FILTER_LEVEL_INFO,
  FILTER_LEVEL_TITLES,
} from "./infoCopy.js";

interface FilterLevelProps {
  axis: FacetAxis;
  /** Current selection for this axis (option keys). */
  value: readonly string[];
  onChange: (next: readonly string[]) => void;
  /** Hide per-option counts; default true (every current level shows them). */
  showCounts?: boolean;
  /**
   * Override the bypass hint label (SB6-ANA-FILT-06 D12 — e.g. Severity
   * shows "No defect class model" instead of "All grades" when the
   * active class model is None). Falls back to the per-axis default.
   */
  bypassLabelOverride?: string;
}

// Resolve the component hover tooltip text from a FacetOption's meta
// (`{ groupName, serialNumber }`). Falls back gracefully.
function componentTooltip(opt: FacetOption): string | null {
  const meta = opt.meta as
    | { groupName?: unknown; groupCode?: unknown; serialNumber?: unknown }
    | undefined;
  if (!meta) return null;
  const name =
    typeof meta.groupName === "string" && meta.groupName.length > 0
      ? meta.groupName
      : typeof meta.groupCode === "string"
        ? meta.groupCode
        : null;
  const sn =
    typeof meta.serialNumber === "string" && meta.serialNumber.length > 0
      ? meta.serialNumber
      : null;
  if (name && sn) return `${name} / SN: ${sn}`;
  if (name) return name;
  if (sn) return `SN: ${sn}`;
  return null;
}

function locationTooltip(opt: FacetOption): string | null {
  const meta = opt.meta as { fullName?: unknown } | undefined;
  if (meta && typeof meta.fullName === "string" && meta.fullName.length > 0) {
    return meta.fullName;
  }
  return null;
}

export function FilterLevel({
  axis,
  value,
  onChange,
  showCounts = true,
  bypassLabelOverride,
}: FilterLevelProps): React.ReactElement {
  const axisKey = axis.axis;
  const title = FILTER_LEVEL_TITLES[axisKey] ?? {
    prefix: "",
    label: axisKey,
  };
  const info = FILTER_LEVEL_INFO[axisKey] ?? "";
  const levelDisabled = axis.disabled;
  const isEmpty = value.length === 0;
  const bypassLabel =
    bypassLabelOverride ?? FILTER_LEVEL_BYPASS[axisKey] ?? "All";

  const validKeys = new Set(axis.options.map((o) => o.key));

  return (
    <Box
      data-testid={`filter-level-${axisKey}`}
      data-axis={axisKey}
      data-level-disabled={levelDisabled ? "true" : "false"}
      role="group"
      aria-label={`${title.label} filter`}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
      }}
    >
      <LevelHeader
        prefix={title.prefix}
        label={title.label}
        info={info}
        axisKey={axisKey}
      />
      <Stack direction="row" spacing={0.5}>
        <ToggleButtonGroup
          size="small"
          value={[...value]}
          disabled={levelDisabled}
          onChange={(_, next) => {
            const arr: string[] = Array.isArray(next)
              ? (next as unknown[]).filter(
                  (v): v is string => typeof v === "string" && validKeys.has(v),
                )
              : [];
            onChange(arr);
          }}
          aria-label={`${title.label} filter`}
        >
          {axis.options.map((opt) => {
            const tooltip =
              axisKey === "component"
                ? componentTooltip(opt)
                : axisKey === "location"
                  ? locationTooltip(opt)
                  : null;
            const button = (
              <ToggleButton
                key={opt.key}
                value={opt.key}
                disabled={opt.disabled || levelDisabled}
                data-filter-value={opt.key}
                data-option-disabled={opt.disabled ? "true" : "false"}
                aria-pressed={value.includes(opt.key)}
                sx={compactToggleSx}
              >
                {opt.label}
                {showCounts ? (
                  <Typography
                    component="span"
                    data-testid={`filter-count-${axisKey}-${opt.key}`}
                    sx={compactCountBadgeSx}
                  >
                    ({opt.count.toString()})
                  </Typography>
                ) : null}
              </ToggleButton>
            );
            if (tooltip) {
              return (
                <Tooltip
                  key={opt.key}
                  title={<Box sx={{ whiteSpace: "pre-line" }}>{tooltip}</Box>}
                  arrow
                >
                  {/* span wrapper keeps the tooltip alive over a
                      disabled button (MUI guidance). */}
                  <span style={{ display: "inline-flex" }}>{button}</span>
                </Tooltip>
              );
            }
            return button;
          })}
        </ToggleButtonGroup>
      </Stack>
      <BypassHintSlot show={isEmpty} label={bypassLabel} />
    </Box>
  );
}
